import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UNSAFE_NavigationContext, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { AutoComplete } from 'primereact/autocomplete';
import { ToastService } from '@iamsoftware/react-hooks';
import { useDataTable } from 'src/hooks/useAppDataTable';
import { AgreementService } from 'src/service/AgreementService';
import { CustomerService } from '../danh-muc/khach-hang/CustomerService';
import { useCustomerDialog } from '../../hooks/useCustomerDialog';
import { AccountInfoService } from '../tai-khoan/thong-tin/AccountInfoService';
import {
  EmploymentContractAgreementRow,
  EmploymentContractField,
  EmploymentContractService,
  EmploymentContractTemplate
} from './EmploymentContractService';
import {
  EmploymentContractSigningAction,
  EmploymentContractSigningDialog
} from './EmploymentContractSigningDialog';
import {
  classifyEmploymentContractCloneError,
  createEmploymentContractCloneError,
  EmploymentContractCloneLoadState,
  EmploymentContractCloneSafeError,
  hasCloneSourceData,
  initializeEmploymentContractClone,
  sanitizeEmploymentContractClonePayload
} from './EmploymentContractClone';
import { EmploymentContractHtmlForm } from './EmploymentContractHtmlForm';
import { Step3Positions } from '../quan-ly-tai-lieu/tai-lieu/components/Step3Positions';
import { PdfCanvasViewer } from '../quan-ly-tai-lieu/PdfCanvasViewer';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

type FieldError = {
  fieldName: string;
  message: string;
};

const EMPLOYMENT_CONTRACT_AGREEMENT_TYPE = 'AgtTypeEmploymentContract';
const EMPLOYER_SIGNER_INDEX = 0;
const EMPLOYEE_SIGNER_INDEX = 1;

const groupLabels: Record<string, string> = {
  company: 'Bên A - Người sử dụng lao động',
  employee: 'Bên B - Người lao động',
  contract: 'Thông tin hợp đồng',
  job: 'Công việc',
  compensation: 'Lương và phụ cấp',
  workingTime: 'Thời giờ làm việc',
  insurance: 'Bảo hiểm'
};

const emptySigner = (sequenceNum = 1) => ({
  partyId: '',
  partyName: '',
  emailAddress: '',
  contactNumber: '',
  roleTypeId: 'AgreementSigner',
  sequenceNum
});

const getCustomerName = (customer: any) =>
  customer?.partyName || customer?.localName || customer?.fullName || customer?.label || '';

const formatCustomerOption = (customer: any) => {
  const partyId = customer?.partyId || customer?.value || '';
  const name = getCustomerName(customer);
  return {
    ...customer,
    value: partyId,
    partyId,
    label: name || partyId
  };
};

const uniqueCustomerOptions = (items: any[] = []) => {
  const seenIds = new Set();
  return items.reduce((acc: any[], item: any) => {
    const option = formatCustomerOption(item);
    const id = option.partyId || option.value;
    if (!id || seenIds.has(id)) return acc;
    seenIds.add(id);
    const { disabled, ...rest } = option;
    acc.push(rest);
    return acc;
  }, []);
};

const isOrganizationCustomer = (customer: any) => {
  const partyTypeEnumId = String(customer?.partyTypeEnumId || '').toLowerCase();
  const partyTypeEnum = String(customer?.partyTypeEnum || '').toLowerCase();
  return partyTypeEnumId === 'ptyorganization' || partyTypeEnum.includes('tổ chức') || partyTypeEnum.includes('to chuc');
};

const isPersonCustomer = (customer: any) => {
  const partyTypeEnumId = String(customer?.partyTypeEnumId || '').toLowerCase();
  const partyTypeEnum = String(customer?.partyTypeEnum || '').toLowerCase();
  return partyTypeEnumId === 'ptyperson' || partyTypeEnum.includes('cá nhân') || partyTypeEnum.includes('ca nhan');
};

const getIdentityCard = (customer: any) =>
  customer?.idValue || customer?.identityCard || (!isOrganizationCustomer(customer) ? (customer?.partyTaxId || customer?.taxCode || '') : '');

const toDateValue = (value: any) => {
  if (!value || value instanceof Date) return value || '';
  const localDate = String(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (localDate) {
    const day = Number(localDate[1]);
    const month = Number(localDate[2]);
    const year = Number(localDate[3]);
    const parsedLocalDate = new Date(year, month - 1, day);
    return parsedLocalDate.getFullYear() === year
      && parsedLocalDate.getMonth() === month - 1
      && parsedLocalDate.getDate() === day
      ? parsedLocalDate
      : value;
  }
  const text = String(value);
  if (!/^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(text)) return value;
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? value : parsed;
};

const mergePartyDefaults = (current: Record<string, any>, employer: any, employee: any) => {
  const next = { ...current };
  const setIfEmpty = (fieldName: string, value: any) => {
    if (value === null || value === undefined || value === '') return;
    const existing = next[fieldName];
    if (existing === null || existing === undefined || String(existing).trim() === '') {
      next[fieldName] = value;
    }
  };

  setIfEmpty('company.name', employer?.partyName);
  setIfEmpty('company.address', employer?.address1 || employer?.address || employer?.partyAddress);
  setIfEmpty('company.phone', employer?.phoneNumber || employer?.contactNumber);
  setIfEmpty('company.representativeName', employer?.representativeName || employer?.fullName);
  setIfEmpty('company.representativeTitle', employer?.representativeJobTitle || employer?.representativeTitle);

  setIfEmpty('employee.fullName', employee?.partyName);
  setIfEmpty('employee.nationality', employee?.nationality || 'Việt Nam');
  setIfEmpty('employee.birthDate', toDateValue(employee?.birthDate));
  setIfEmpty('employee.birthPlace', employee?.birthPlace);
  setIfEmpty('employee.permanentAddress', employee?.permanentAddress || employee?.address1 || employee?.address || employee?.partyAddress);
  setIfEmpty('employee.temporaryAddress', employee?.temporaryAddress);
  setIfEmpty('employee.idNumber', getIdentityCard(employee));
  setIfEmpty('employee.idIssuedDate', toDateValue(employee?.idIssuedDate));
  setIfEmpty('employee.idIssuedPlace', employee?.idIssuedPlace);

  return next;
};

const formatSeq = (value: number) => value < 10 ? `0${value}` : String(value);

const createSteps = [
  { label: 'Đối tượng', icon: 'pi pi-users' },
  { label: 'Dữ liệu', icon: 'pi pi-list-check' },
  { label: 'Vị trí ký', icon: 'pi pi-map-marker' }
];

const fieldIsDate = (field?: EmploymentContractField) =>
  field?.fieldTypeEnumId === 'DBFFAT_date-short' || field?.fieldTypeEnumId === 'DBFFAT_date-long';

const isValidDateValue = (value: any) => {
  const parsed = toDateValue(value);
  return parsed instanceof Date && !Number.isNaN(parsed.getTime());
};

const statusSeverity = (statusId?: string): any => {
  if (statusId === 'AGT_DRAFT') return 'secondary';
  if (statusId === 'AGT_SIGN_COMPLETED') return 'success';
  if (statusId === 'AGT_CANCELLED') return 'danger';
  if (statusId === 'AGT_DELETED') return 'danger';
  return 'info';
};

const renderSigningStatus = (
  employerSigned = false,
  employeeSigned = false
) => {
  return (
    <div className="flex align-items-center flex-wrap gap-2">
      <Tag
        value={employerSigned ? 'Bên A: Đã ký' : 'Bên A: Chưa ký'}
        icon={employerSigned ? 'pi pi-check-circle' : 'pi pi-clock'}
        style={{
          backgroundColor: employerSigned ? '#16a34a' : '#f59e0b',
          color: '#ffffff'
        }}
      />
      <Tag
        value={employeeSigned ? 'Bên B: Đã ký' : 'Bên B: Chưa ký'}
        icon={employeeSigned ? 'pi pi-check-circle' : 'pi pi-clock'}
        style={{
          backgroundColor: employeeSigned ? '#0891b2' : '#64748b',
          color: '#ffffff'
        }}
      />
    </div>
  );
};

const getSignedPartyCount = (agreement: EmploymentContractAgreementRow) =>
  Math.max(0, Number(agreement?.partyCount || 0));

const isSigningClosed = (statusId?: string) =>
  ['AGT_SIGN_COMPLETED', 'AGT_COMPLETED', 'AGT_CANCELLED', 'AGT_DELETED'].includes(statusId || '');

const toApiDate = (value: any) => {
  if (!value) return '';
  if (!(value instanceof Date)) return value;
  const day = String(value.getDate()).padStart(2, '0');
  const month = String(value.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${value.getFullYear()}`;
};

export const EmploymentContract = () => {
  const navigate = useNavigate();
  const [signingDialog, setSigningDialog] = useState<{
    action: EmploymentContractSigningAction;
    agreement: EmploymentContractAgreementRow;
  } | null>(null);
  const [contractViewer, setContractViewer] = useState<{
    agreementId: string;
    agreementName: string;
    fileId: string;
    fileName: string;
  } | null>(null);
  const [viewerPage, setViewerPage] = useState(1);

  const openContractViewer = async (agreement: EmploymentContractAgreementRow) => {
    try {
      const result = await AgreementService.getAgreementFiles(agreement.agreementId);
      const mainFile = result?.mainFiles?.find((file: any) => file?.referenceFileId)
        || result?.mainFiles?.[0];
      const fileId = mainFile?.referenceFileId;

      if (!fileId) {
        ToastService.error('Bản PDF của hợp đồng chưa sẵn sàng');
        return;
      }

      setViewerPage(1);
      setContractViewer({
        agreementId: agreement.agreementId,
        agreementName: agreement.agreementName || 'Xem hợp đồng',
        fileId,
        fileName: mainFile.fileName || `${agreement.agreementName || 'hop-dong'}.pdf`
      });
    } catch (error) {
      console.error('Không thể mở hợp đồng:', error);
      ToastService.error('Không thể tải file hợp đồng');
    }
  };

  const closeContractViewer = () => {
    setContractViewer(null);
    setViewerPage(1);
  };

  const columns: any[] = [
    {
      field: 'agreementName',
      header: 'Tên hợp đồng',
      width: 220,
      matchMode: 'contains',
      dataType: 'custom' as const,
      customCell(rowData: any): any {
        return (
          <span
            className="underline text-primary cursor-pointer"
            onClick={() => navigate(`/hop-dong-lao-dong/${rowData.agreementId}`)}
            title="Xem chi tiết"
          >
            {rowData.agreementName}
          </span>
        );
      }
    },
    { field: 'agreementNo', header: 'Số hợp đồng', width: 140, matchMode: 'contains' },
    {
      field: 'statusDescription',
      header: 'Trạng thái',
      width: 130,
      dataType: 'custom' as const,
      customCell(rowData: any): any {
        return <Tag value={rowData.statusDescription || rowData.statusId} severity={statusSeverity(rowData.statusId)} />;
      }
    },
    {
      field: 'partyCount',
      header: 'Trạng thái ký',
      width: 250,
      dataType: 'custom' as const,
      customCell(rowData: EmploymentContractAgreementRow): any {
        const signedPartyCount = getSignedPartyCount(rowData);
        return renderSigningStatus(signedPartyCount >= 1, signedPartyCount >= 2);
      }
    },
    { field: 'agreementTypeName', header: 'Loại tài liệu', width: 180, matchMode: 'contains' },
    {
      field: 'contentCount',
      header: 'Số file',
      width: 90,
      dataType: 'custom' as const,
      customCell(rowData: any): any {
        return <Tag value={`${rowData.contentCount || 0} file`} severity="info" />;
      }
    }
  ];

  const { render: renderEmploymentContracts, reloadLazyData } = useDataTable({
    recordLabel: 'Hợp đồng lao động',
    headerActions: (
      <Button
        label="Tạo hợp đồng"
        icon="pi pi-plus"
        onClick={() => navigate('/hop-dong-lao-dong/tao-moi')}
      />
    ),
    dataKey: 'agreementId',
    indexColumnWidth: 56,
    actionColumnWidth: 180,
    columns,
    getList: lazyLoadEvent => {
      return AgreementService.getAgreementList(
        lazyLoadEvent,
        undefined,
        EMPLOYMENT_CONTRACT_AGREEMENT_TYPE
      ).then(result => ({
        listData: result.listData || [],
        totalRecords: result.totalRecords || (result.listData || []).length
      }));
    },
    initActions(items: Array<any>) {
      if (!items) return;
      items.forEach(item => {
        const actions: any[] = [];
        const signedPartyCount = getSignedPartyCount(item);
        const signingClosed = isSigningClosed(item.statusId);
        if (item.statusId === 'AGT_DRAFT' || item.statusId === 'AGT_INIT_DRAFT') {
          actions.push({
            icon: 'pi pi-pencil',
            label: 'Chỉnh sửa',
            title: 'Chỉnh sửa hợp đồng',
            command: () => navigate(`/hop-dong-lao-dong/${item.agreementId}/chinh-sua`)
          });
        }
        if (!signingClosed && signedPartyCount === 0) {
          actions.push({
            icon: 'pi pi-pen-to-square',
            label: 'Ký hợp đồng',
            title: 'Ký hợp đồng',
            command: () => setSigningDialog({ action: 'employer-sign', agreement: item })
          });
        }
        if (!signingClosed && signedPartyCount === 1) {
          actions.push({
            icon: 'pi pi-send',
            label: 'Gửi email',
            title: 'Gửi email cho bên B',
            command: () => setSigningDialog({ action: 'send-email', agreement: item })
          });
        }
        actions.push({
          icon: 'pi pi-copy',
          label: 'Nhân bản hợp đồng',
          title: 'Nhân bản hợp đồng',
          props: { 'aria-label': 'Nhân bản hợp đồng' },
          command: () => navigate(`/hop-dong-lao-dong/${item.agreementId}/nhan-ban`)
        });
        actions.push({
          icon: 'pi pi-eye',
          label: 'Xem hợp đồng',
          title: 'Xem hợp đồng',
          command: () => openContractViewer(item)
        });
        item.actions = actions;
      });
    },
    originalProps: { scrollHeight: 'calc(100vh - 245px)' }
  });

  return (
    <div>
      <div className="tai-lieu-ky-table">
        {renderEmploymentContracts()}
      </div>
      <EmploymentContractSigningDialog
        visible={Boolean(signingDialog)}
        action={signingDialog?.action || 'employer-sign'}
        agreement={signingDialog?.agreement}
        onHide={() => setSigningDialog(null)}
        onSuccess={reloadLazyData}
      />
      <Dialog
        header={<span className="font-semibold text-lg">{contractViewer?.agreementName || 'Xem hợp đồng'}</span>}
        visible={Boolean(contractViewer)}
        style={{ width: '98vw', height: '95vh' }}
        contentClassName="p-0 flex flex-column"
        maximizable
        onHide={closeContractViewer}
        footer={(
          <div className="flex justify-content-end">
            <Button
              label="Đóng"
              icon="pi pi-times"
              onClick={closeContractViewer}
              className="p-button-text"
            />
          </div>
        )}
      >
        {contractViewer && (
          <PdfCanvasViewer
            fileId={contractViewer.fileId}
            fileName={contractViewer.fileName}
            agreementId={contractViewer.agreementId}
            scale={2}
            currentPage={viewerPage}
            onPageChange={setViewerPage}
            enableZoom
          />
        )}
      </Dialog>
    </div>
  );
};

type EmploymentContractCreateProps = {
  readOnly?: boolean;
};

export const EmploymentContractCreate: React.FC<EmploymentContractCreateProps> = ({ readOnly = false }) => {
  const navigate = useNavigate();
  const navigationContext = React.useContext(UNSAFE_NavigationContext);
  const { agreementId, sourceAgreementId } = useParams();
  const isEditing = Boolean(agreementId) && !readOnly;
  const isCloning = Boolean(sourceAgreementId) && !readOnly;
  const [templates, setTemplates] = useState<EmploymentContractTemplate[]>([]);
  const [templateCode, setTemplateCode] = useState<string>('');
  const [templateName, setTemplateName] = useState<string>('');
  const [fields, setFields] = useState<EmploymentContractField[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [formTemplateHtml, setFormTemplateHtml] = useState('');
  const [formTemplateLoading, setFormTemplateLoading] = useState(false);
  const [agreementName, setAgreementName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [signers, setSigners] = useState<any[]>([emptySigner(1), emptySigner(2)]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [employeeParty, setEmployeeParty] = useState<any>(null);
  const [signaturePositions, setSignaturePositions] = useState<any[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedSigner, setSelectedSigner] = useState<any>(null);
  const [selectedSignatureType, setSelectedSignatureType] = useState('AgtDigitalVerified');
  const [pageCanvases, setPageCanvases] = useState<any[]>([]);
  const [canvasLoading, setCanvasLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMainFiles, setPreviewMainFiles] = useState<any[]>([]);
  const [exitDialogVisible, setExitDialogVisible] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const [detailSigningAction, setDetailSigningAction] = useState<EmploymentContractSigningAction | null>(null);
  const [cloneLoadState, setCloneLoadState] = useState<EmploymentContractCloneLoadState>(isCloning ? 'loading' : 'ready');
  const [cloneError, setCloneError] = useState<EmploymentContractCloneSafeError | null>(null);
  const [cloneSource, setCloneSource] = useState({ agreementName: '', agreementNo: '', statusId: '' });
  const [cloneRetryKey, setCloneRetryKey] = useState(0);
  const canvasRefs = useRef<any[]>([]);
  const draggableRefs = useRef<Record<string, React.RefObject<any>>>({});
  const editDraftRef = useRef<any>(null);
  const hasUnsavedChangesRef = useRef(false);
  const pendingNavigationRef = useRef<null | (() => void)>(null);
  const submitInFlightRef = useRef(false);

  const markUnsavedChanges = useCallback(() => {
    if (!readOnly) hasUnsavedChangesRef.current = true;
  }, [readOnly]);

  const clearUnsavedChanges = useCallback(() => {
    hasUnsavedChangesRef.current = false;
  }, []);

  useEffect(() => {
    if (readOnly) return;

    const navigator = navigationContext.navigator as any;
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    const originalGo = navigator.go;
    if (!originalPush || !originalReplace || !originalGo) return;

    const guardNavigation = (navigation: () => void) => {
      if (!hasUnsavedChangesRef.current) {
        navigation();
        return;
      }
      pendingNavigationRef.current = navigation;
      setExitDialogVisible(true);
    };
    const guardedPush = (...args: any[]) => guardNavigation(() => originalPush.apply(navigator, args));
    const guardedReplace = (...args: any[]) => guardNavigation(() => originalReplace.apply(navigator, args));
    const guardedGo = (...args: any[]) => guardNavigation(() => originalGo.apply(navigator, args));

    navigator.push = guardedPush;
    navigator.replace = guardedReplace;
    navigator.go = guardedGo;

    return () => {
      if (navigator.push === guardedPush) navigator.push = originalPush;
      if (navigator.replace === guardedReplace) navigator.replace = originalReplace;
      if (navigator.go === guardedGo) navigator.go = originalGo;
    };
  }, [navigationContext.navigator, readOnly]);

  useEffect(() => {
    if (readOnly) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChangesRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [readOnly]);

  useEffect(() => {
    if (isCloning) return;
    EmploymentContractService.getTemplates()
      .then(res => {
        const list = res?.templates || [];
        setTemplates(list);
        if (!agreementId && list.length > 0) setTemplateCode(list[0].templateCode);
      })
      .catch(() => ToastService.error('Không tải được danh sách mẫu hợp đồng lao động'));
  }, [agreementId, isCloning]);

  useEffect(() => {
    if (!templateCode || isCloning) return;
    EmploymentContractService.getTemplateMetadata(templateCode)
      .then(res => {
        const nextFields = (res?.fields || []).sort((a, b) => Number(a.sequenceNum || 0) - Number(b.sequenceNum || 0));
        const defaults: Record<string, any> = {};
        nextFields.forEach(field => {
          defaults[field.fieldName] = field.defaultValue || '';
        });
        const savedDraft = editDraftRef.current;
        if (savedDraft?.templateCode === templateCode && savedDraft?.data) {
          Object.entries(savedDraft.data).forEach(([fieldName, value]) => {
            const field = nextFields.find(item => item.fieldName === fieldName);
            defaults[fieldName] = fieldIsDate(field) ? toDateValue(value) : value;
          });
        }
        setFields(nextFields);
        setTemplateName(res?.templateName || '');
        setAgreementName(savedDraft?.agreement?.agreementName || res?.templateName || '');
        setFormData(defaults);
        setFieldErrors({});
      })
      .catch(() => ToastService.error('Không tải được metadata mẫu hợp đồng lao động'));
  }, [isCloning, templateCode]);

  useEffect(() => {
    if (isCloning) return;
    if (!templateCode) {
      setFormTemplateHtml('');
      return;
    }
    setFormTemplateLoading(true);
    EmploymentContractService.getFormTemplate(templateCode)
      .then(response => {
        if (response?.success === 'N' || !response?.formHtml) {
          setFormTemplateHtml('');
          ToastService.error(response?.error || 'Mẫu hợp đồng chưa có giao diện nhập liệu');
          return;
        }
        setFormTemplateHtml(response.formHtml);
      })
      .catch(() => {
        setFormTemplateHtml('');
        ToastService.error('Không tải được giao diện nhập liệu hợp đồng');
      })
      .finally(() => setFormTemplateLoading(false));
  }, [isCloning, templateCode]);

  const groupedFields = useMemo(() => {
    return fields.reduce((acc: Record<string, EmploymentContractField[]>, field) => {
      const group = field.fieldGroup || 'other';
      if (!acc[group]) acc[group] = [];
      acc[group].push(field);
      return acc;
    }, {});
  }, [fields]);

  const fieldProgress = useMemo(() => {
    const completed = fields.filter(field => {
      const value = formData[field.fieldName];
      if (fieldIsDate(field)) return isValidDateValue(value);
      return value !== null && value !== undefined && String(value).trim() !== '';
    }).length;

    return {
      completed,
      total: fields.length,
      percentage: fields.length > 0 ? Math.round((completed / fields.length) * 100) : 0
    };
  }, [fields, formData]);

  const documentRecipients = useMemo(() => signers
    .map((signer, index) => {
      const partyId = signer?.partyId || signer?.party?.partyId || signer?.party?.value || '';
      const partyName = signer?.partyName || signer?.party?.label || '';
      return {
        ...signer,
        _internalId: `employment-signer-${index}`,
        partyId,
        partyName,
        roleTypeId: 'AgreementSigner',
        party: {
          ...(signer?.party || {}),
          value: partyId,
          partyId,
          label: partyName
        }
      };
    })
    .filter(recipient => recipient.partyId), [signers]);

  useEffect(() => {
    if (documentRecipients.length === 0) {
      setSelectedSigner(null);
      return;
    }
    setSelectedSigner((current: any) => {
      const currentId = current?.partyId || current?.value;
      const matching = documentRecipients.find(recipient => String(recipient.partyId) === String(currentId || ''));
      const recipient = matching || documentRecipients[0];
      return {
        _internalId: recipient._internalId,
        partyId: recipient.partyId,
        value: recipient.partyId,
        roleTypeId: 'AgreementSigner',
        label: recipient.partyName
      };
    });
  }, [documentRecipients]);

  const setFieldValue = (fieldName: string, value: any) => {
    markUnsavedChanges();
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (fieldName === 'employee.idNumber') {
      setSigners(prev => {
        const next = [prev[0] || emptySigner(1), prev[1] || emptySigner(2)];
        next[EMPLOYEE_SIGNER_INDEX] = {
          ...next[EMPLOYEE_SIGNER_INDEX],
          identityCard: value || '',
          roleTypeId: 'AgreementSigner',
          sequenceNum: EMPLOYEE_SIGNER_INDEX + 1
        };
        return next;
      });
    }
    setFieldErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const updateSigner = useCallback((index: number, patch: Record<string, any>, markAsDirty = true) => {
    if (markAsDirty) markUnsavedChanges();
    setSigners(prev => {
      const next = [prev[0] || emptySigner(1), prev[1] || emptySigner(2)];
      next[index] = {
        ...next[index],
        ...patch,
        roleTypeId: 'AgreementSigner',
        sequenceNum: index + 1
      };
      return next;
    });
  }, [markUnsavedChanges]);

  const mapCustomerToSigner = useCallback((customer: any, index: number) => ({
    partyStatusId: customer?.partyStatusId || customer?.statusId,
    statusId: customer?.statusId,
    partyId: customer?.partyId || customer?.value || '',
    partyName: getCustomerName(customer),
    emailAddress: customer?.emailAddress || '',
    contactNumber: customer?.phoneNumber || customer?.contactNumber || '',
    identityCard: getIdentityCard(customer),
    representativeName: customer?.representativeName || customer?.fullName || '',
    representativeTitle: customer?.representativeJobTitle || customer?.representativeTitle || '',
    representativeNationality: customer?.representativeNationality || 'Việt Nam',
    address: customer?.address1 || customer?.address || customer?.partyAddress || '',
    pseudoId: customer?.pseudoId || customer?.partyTaxId || customer?.taxCode || '',
    partyTypeEnumId: customer?.partyTypeEnumId,
    roleTypeId: 'AgreementSigner',
    sequenceNum: index + 1,
    party: formatCustomerOption(customer)
  }), []);

  useEffect(() => {
    if (!isCloning || !sourceAgreementId) return;
    let cancelled = false;

    const initializeClone = async () => {
      setCloneLoadState('loading');
      setCloneError(null);
      setFormTemplateLoading(true);
      setFields([]);
      setFormTemplateHtml('');
      try {
        const [source, templateResponse, accountInfo] = await Promise.all([
          EmploymentContractService.getAgreement(sourceAgreementId, false),
          EmploymentContractService.getTemplates(),
          AccountInfoService.getInfo()
        ]);
        if (cancelled) return;
        if (source?.success === 'N' || !source?.agreement || !source?.templateCode) {
          throw createEmploymentContractCloneError('SOURCE_UNAVAILABLE');
        }
        if (!hasCloneSourceData(source.data)) {
          throw createEmploymentContractCloneError('SOURCE_DATA_EMPTY');
        }

        const activeTemplates = templateResponse?.templates || [];
        const sourceTemplate = activeTemplates.find(item => item.templateCode === source.templateCode);
        if (!sourceTemplate) {
          throw createEmploymentContractCloneError('TEMPLATE_UNAVAILABLE');
        }

        const employerInfo = {
          ...accountInfo,
          partyTypeEnumId: accountInfo?.partyTypeEnumId || 'PtyOrganization',
          partyId: accountInfo?.partyId || accountInfo?.organizationPartyId || accountInfo?.ownerPartyId || '',
          partyName: accountInfo?.partyName || '',
          partyTaxId: accountInfo?.partyTaxId || accountInfo?.pseudoId || '',
          contactNumber: accountInfo?.phoneNumber || accountInfo?.contactNumber || ''
        };
        const currentEmployer = mapCustomerToSigner(employerInfo, EMPLOYER_SIGNER_INDEX);
        if (!currentEmployer.partyId) {
          throw createEmploymentContractCloneError('CURRENT_EMPLOYER_UNAVAILABLE');
        }

        const [metadata, formTemplate] = await Promise.all([
          EmploymentContractService.getTemplateMetadata(source.templateCode),
          EmploymentContractService.getFormTemplate(source.templateCode)
        ]);
        if (cancelled) return;
        if (metadata?.templateCode !== source.templateCode || !Array.isArray(metadata?.fields)
          || formTemplate?.success === 'N' || !formTemplate?.formHtml) {
          throw createEmploymentContractCloneError('TEMPLATE_UNAVAILABLE');
        }

        const nextFields = [...metadata.fields]
          .sort((a, b) => Number(a.sequenceNum || 0) - Number(b.sequenceNum || 0));
        const seed = initializeEmploymentContractClone(source, nextFields, currentEmployer);
        const normalizedFormData = Object.entries(seed.formData).reduce((result, [fieldName, value]) => {
          const field = nextFields.find(item => item.fieldName === fieldName);
          result[fieldName] = fieldIsDate(field) && value ? toDateValue(value) : value;
          return result;
        }, {} as Record<string, any>);

        editDraftRef.current = null;
        setTemplates(activeTemplates);
        setTemplateCode(seed.templateCode);
        setTemplateName(metadata.templateName || sourceTemplate.templateName || '');
        setFields(nextFields);
        setFormData(normalizedFormData);
        setFormTemplateHtml(formTemplate.formHtml);
        setAgreementName(seed.agreementName);
        setSigners([seed.employerSigner, seed.employeeSigner]);
        setEmployeeParty(null);
        setSignaturePositions(seed.signaturePositions);
        setFieldErrors({});
        setActiveStep(0);
        setCloneSource({
          agreementName: seed.sourceAgreementName,
          agreementNo: seed.sourceAgreementNo,
          statusId: seed.sourceStatusId
        });
        clearUnsavedChanges();
        setCloneLoadState('ready');
      } catch (error: any) {
        if (cancelled) return;
        setCloneError(classifyEmploymentContractCloneError(error));
        setCloneLoadState('error');
      } finally {
        if (!cancelled) setFormTemplateLoading(false);
      }
    };

    initializeClone();
    return () => {
      cancelled = true;
    };
  }, [clearUnsavedChanges, cloneRetryKey, isCloning, mapCustomerToSigner, sourceAgreementId]);

  const selectPartyForSigner = useCallback(async (index: number, option: any) => {
    if (!option) {
      updateSigner(index, emptySigner(index + 1));
      if (index === EMPLOYEE_SIGNER_INDEX) setEmployeeParty(null);
      return;
    }

    const partyId = option.partyId || option.value;
    let customer = option;
    if (partyId) {
      try {
        customer = await CustomerService.get(partyId);
      } catch (e) {
        customer = option;
      }
    }

    if (index === EMPLOYEE_SIGNER_INDEX && !isPersonCustomer(customer)) {
      ToastService.error('Người lao động phải là cá nhân');
      setEmployeeParty(null);
      return;
    }

    const signer = mapCustomerToSigner({ ...option, ...customer, partyId }, index);
    updateSigner(index, signer);
    if (index === EMPLOYEE_SIGNER_INDEX) {
      setEmployeeParty(formatCustomerOption({ ...option, ...customer, partyId }));
    }
  }, [mapCustomerToSigner, updateSigner]);

  useEffect(() => {
    if (!agreementId) return;

    EmploymentContractService.getAgreement(agreementId, !readOnly)
      .then(async response => {
        if (response?.success === 'N') {
          ToastService.error(response?.error || 'Không tải được hợp đồng lao động');
          navigate('/hop-dong-lao-dong');
          return;
        }

        editDraftRef.current = response;
        setAgreementName(response?.agreement?.agreementName || '');
        setTemplateCode(response?.templateCode || '');

        const parties = (response?.agreementParties || []).slice(0, 2);
        const hydratedParties = await Promise.all(parties.map(async (party: any, index: number) => {
          const partyId = party?.partyId;
          let customer = party;
          if (partyId) {
            try {
              customer = await CustomerService.get(partyId);
            } catch (e) {
              customer = party;
            }
          }
          const savedEmployeeIdentityCard = index === EMPLOYEE_SIGNER_INDEX
            ? response?.data?.['employee.idNumber'] || ''
            : '';
          const identityCard = savedEmployeeIdentityCard || party?.identityCard || getIdentityCard(customer);
          const hydratedParty = {
            ...customer,
            ...party,
            partyId,
            emailAddress: party?.emailAddress || customer?.emailAddress || '',
            contactNumber: party?.contactNumber || customer?.contactNumber || customer?.phoneNumber || '',
            identityCard,
            idValue: identityCard || customer?.idValue || ''
          };
          return {
            raw: hydratedParty,
            signer: mapCustomerToSigner(hydratedParty, index)
          };
        }));

        const restoredSigners = [
          hydratedParties[EMPLOYER_SIGNER_INDEX]?.signer || emptySigner(1),
          hydratedParties[EMPLOYEE_SIGNER_INDEX]?.signer || emptySigner(2)
        ];
        setSigners(restoredSigners);
        if (hydratedParties[EMPLOYEE_SIGNER_INDEX]?.raw) {
          setEmployeeParty(formatCustomerOption(hydratedParties[EMPLOYEE_SIGNER_INDEX].raw));
        }

        setSignaturePositions((response?.signaturePositions || []).map((position: any, index: number) => ({
          ...position,
          locationSeqId: position.locationSeqId || formatSeq(index + 1),
          pageIndex: Math.max(0, Number(position.pageNumber || 1) - 1),
          x: Number(position.ulx || 0),
          y: Number(position.uly || 0),
          widthPx: Number(position.widthPx || 200),
          heightPx: Number(position.heightPx || 65),
          signerIndex: restoredSigners.findIndex(
            signer => String(signer.partyId || '') === String(position.partyId || '')
          )
        })));
      })
      .catch((error: any) => {
        ToastService.error(error?.response?.data?.error || 'Không tải được hợp đồng lao động');
        navigate('/hop-dong-lao-dong');
      });
  }, [agreementId, detailRefreshKey, mapCustomerToSigner, navigate, readOnly]);

  const searchCustomers = (event: any) => {
    CustomerService.find(event?.query || '')
      .then((data: any) => setCustomers(uniqueCustomerOptions(data?.listData || []).filter(isPersonCustomer)))
      .catch(() => { });
  };

  const customerDialog = useCustomerDialog({
    service: CustomerService,
    requireTaxCode: false,
    dialogHeader: 'nhân viên',
    partyTypeOptions: [{ value: 'PtyPerson', label: 'Cá nhân' }],
    defaultPartyTypeEnumId: 'PtyPerson',
    lockPartyType: true,
    async onAfterCreate(resp: any) {
      const customerData = await CustomerService.find('');
      const options = uniqueCustomerOptions(customerData?.listData || []).filter(isPersonCustomer);
      setCustomers(options);
      const created = options.find((customer: any) => String(customer.partyId) === String(resp.partyId));
      if (created) {
        await selectPartyForSigner(EMPLOYEE_SIGNER_INDEX, created);
      }
    }
  });

  const createEmployee = () => {
    const employee = signers[EMPLOYEE_SIGNER_INDEX] || {};
    customerDialog.create({
      partyTypeEnumId: 'PtyPerson',
      partyName: employee.partyName || '',
      emailAddress: employee.emailAddress || '',
      contactNumber: employee.contactNumber || '',
      idValue: employee.identityCard || ''
    });
  };

  useEffect(() => {
    CustomerService.find('')
      .then((data: any) => {
        const options = uniqueCustomerOptions(data?.listData || []).filter(isPersonCustomer);
        setCustomers(options);
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (isCloning) return;
    AccountInfoService.getInfo()
      .then((accountInfo: any) => {
        const employerInfo = {
          ...accountInfo,
          partyTypeEnumId: accountInfo?.partyTypeEnumId || 'PtyOrganization',
          partyId: accountInfo?.partyId || accountInfo?.organizationPartyId || accountInfo?.ownerPartyId || '',
          partyName: accountInfo?.partyName || '',
          partyTaxId: accountInfo?.partyTaxId || accountInfo?.pseudoId || '',
          contactNumber: accountInfo?.phoneNumber || accountInfo?.contactNumber || ''
        };
        updateSigner(EMPLOYER_SIGNER_INDEX, mapCustomerToSigner(employerInfo, EMPLOYER_SIGNER_INDEX), false);
      })
      .catch(() => ToastService.error('Không tải được thông tin đơn vị hiện tại'));
  }, [isCloning, mapCustomerToSigner, updateSigner]);

  useEffect(() => {
    if (fields.length === 0) return;
    setFormData(prev => {
      const employee = signers[EMPLOYEE_SIGNER_INDEX];
      const next = mergePartyDefaults(
        prev,
        signers[EMPLOYER_SIGNER_INDEX],
        isCloning && !employee?.partyId ? null : employee
      );
      if (isCloning && !employee?.partyId) {
        fields.filter(field => field.fieldGroup === 'employee').forEach(field => {
          next[field.fieldName] = '';
        });
      }
      return next;
    });
  }, [fields, isCloning, signers]);

  const normalizeLocationSeq = useCallback((positions: any[]) => positions.map((position, index) => ({
    ...position,
    locationSeqId: formatSeq(index + 1)
  })), []);

  const setSignaturePositionsWithDirty = useCallback((next: React.SetStateAction<any[]>) => {
    markUnsavedChanges();
    setSignaturePositions(next);
  }, [markUnsavedChanges]);

  const renderPdfPage = useCallback(async (pdf: any, pageNumber: number) => {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: context, viewport }).promise;
    return canvas;
  }, []);

  const loadPreviewPdf = useCallback(async (pdfDataBase64: string) => {
    setCanvasLoading(true);
    setPageCanvases([]);
    canvasRefs.current = [];
    try {
      const encoded = String(pdfDataBase64 || '').replace(/^data:application\/pdf;base64,/, '');
      const binary = window.atob(encoded);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }

      const pdf = await getDocument({ data: bytes }).promise;
      const canvases = await Promise.all(
        Array.from({ length: pdf.numPages }, (_, index) => renderPdfPage(pdf, index + 1))
      );
      setPageCanvases(canvases);
    } finally {
      setCanvasLoading(false);
    }
  }, [renderPdfPage]);

  const handleDrop = (event: React.DragEvent, pageIndex: number) => {
    const canvas = canvasRefs.current[pageIndex];
    const rect = canvas?.getBoundingClientRect();
    if (!canvas || !rect || !selectedSigner) return;

    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
    const canvasWidth = Number(canvas.width || rect.width);
    const canvasHeight = Number(canvas.height || rect.height);
    const scaleX = rect.width > 0 ? canvasWidth / rect.width : 1;
    const scaleY = rect.height > 0 ? canvasHeight / rect.height : 1;
    const signatureWidth = 200;
    const signatureHeight = selectedSignatureType === 'AgtDigitalVerified' ? 65 : 100;
    const cursorX = (event.clientX - rect.left) * scaleX;
    const cursorY = (event.clientY - rect.top) * scaleY;
    const partyId = selectedSigner.partyId || selectedSigner.value;
    const recipient = documentRecipients.find(item => String(item.partyId) === String(partyId));

    setSignaturePositionsWithDirty(prev => normalizeLocationSeq([...prev, {
      x: clamp(cursorX, 0, Math.max(0, canvasWidth - signatureWidth)),
      y: clamp(cursorY, 0, Math.max(0, canvasHeight - signatureHeight)),
      pageIndex,
      pageWidth: canvasWidth,
      pageHeight: canvasHeight,
      widthPx: signatureWidth,
      heightPx: signatureHeight,
      fontSize: 12,
      signatureTypeEnumId: selectedSignatureType,
      label: recipient?.partyName || selectedSigner.label || '',
      partyName: recipient?.partyName || selectedSigner.label || '',
      partyId,
      internalRecipientId: recipient?._internalId,
      roleTypeId: 'AgreementSigner',
      fileId: 'employment-contract-preview'
    }]));
  };

  const handleDragStop = (_event: any, data: any, locationSeqId: string) => {
    setSignaturePositionsWithDirty(prev => normalizeLocationSeq(prev.map(position => {
      if (position.locationSeqId !== locationSeqId) return position;
      const canvas = canvasRefs.current[position.pageIndex];
      if (!canvas) return position;
      const width = Number(position.widthPx || 200);
      const height = Number(position.heightPx || 65);
      const x = Math.min(Math.max(Number(data.x) || 0, 0), Math.max(0, canvas.width - width));
      const y = Math.min(Math.max(Number(data.y) || 0, 0), Math.max(0, canvas.height - height));
      return { ...position, x, y, pageWidth: canvas.width, pageHeight: canvas.height };
    })));
  };

  const removeSignaturePosition = (locationSeqId: string) => {
    setSignaturePositionsWithDirty(prev => normalizeLocationSeq(
      prev.filter(position => position.locationSeqId !== locationSeqId)
    ));
  };

  const hasValidSignaturePositions = () => {
    if (signaturePositions.length === 0) {
      ToastService.error('Vui lòng đặt vị trí ký cho hai bên');
      return false;
    }

    const signerIds = signers.map(signer => signer?.partyId).filter(Boolean).map(String);
    const invalidPosition = signaturePositions.some(position => !position.partyId || !signerIds.includes(String(position.partyId)));
    if (invalidPosition) {
      ToastService.error('Vị trí ký chưa gắn đúng người ký');
      return false;
    }

    const signerWithoutPosition = signerIds.find(partyId =>
      !signaturePositions.some(position => String(position.partyId) === partyId)
    );
    if (signerWithoutPosition) {
      ToastService.error('Mỗi bên cần có ít nhất một vị trí ký');
      return false;
    }
    return true;
  };

  const validateLocal = () => {
    const errors: Record<string, string> = {};
    fields.forEach(field => {
      const value = formData[field.fieldName];
      if (field.isRequired === 'Y' && (value === null || value === undefined || String(value).trim() === '')) {
        errors[field.fieldName] = `Vui lòng nhập ${field.fieldLabel || field.fieldName}`;
      } else if (fieldIsDate(field) && value && !isValidDateValue(value)) {
        errors[field.fieldName] = `${field.fieldLabel || field.fieldName} phải đúng định dạng dd/MM/yyyy`;
      }
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return false;

    const employee = signers[EMPLOYEE_SIGNER_INDEX];
    if (!employee?.partyId) {
      ToastService.error('Vui lòng chọn hoặc tạo người lao động');
      return false;
    }

    return hasValidSignaturePositions();
  };

  const validateCurrentStep = () => {
    if (readOnly) return true;

    if (activeStep === 0) {
      const employee = signers[EMPLOYEE_SIGNER_INDEX];
      if (!employee?.partyId) {
        ToastService.error('Vui lòng chọn hoặc tạo người lao động');
        return false;
      }
      return true;
    }

    if (activeStep === 1) {
      if (!templateCode) {
        ToastService.error('Vui lòng chọn mẫu hợp đồng');
        return false;
      }
      const errors: Record<string, string> = {};
      fields.forEach(field => {
        const value = formData[field.fieldName];
        if (field.isRequired === 'Y' && (value === null || value === undefined || String(value).trim() === '')) {
          errors[field.fieldName] = `Vui lòng nhập ${field.fieldLabel || field.fieldName}`;
        } else if (fieldIsDate(field) && value && !isValidDateValue(value)) {
          errors[field.fieldName] = `${field.fieldLabel || field.fieldName} phải đúng định dạng dd/MM/yyyy`;
        }
      });
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) {
        ToastService.error('Vui lòng nhập đủ dữ liệu bắt buộc');
        return false;
      }
      return true;
    }

    return hasValidSignaturePositions();
  };

  const goNext = async () => {
    if (!validateCurrentStep()) return;

    if (activeStep === 1) {
      setPreviewLoading(true);
      try {
        const payload = buildPayload();
        const preview = await EmploymentContractService.previewTemplate(templateCode, {
          agreementName: agreementName || templateName,
          data: payload.data
        });
        if (preview?.success === 'N' || !preview?.pdfDataBase64) {
          const errors = (preview?.fieldErrors || []) as FieldError[];
          if (errors.length > 0) {
            setFieldErrors(errors.reduce((acc, error) => ({ ...acc, [error.fieldName]: error.message }), {}));
          }
          ToastService.error(preview?.error || 'Không tạo được bản xem trước hợp đồng');
          return;
        }

        await loadPreviewPdf(preview.pdfDataBase64);
        setPreviewMainFiles([{
          name: preview.pdfFileName || `${agreementName || templateName}.pdf`,
          fileName: preview.pdfFileName || `${agreementName || templateName}.pdf`,
          fileId: 'employment-contract-preview',
          contentType: 'application/pdf',
          type: 'application/pdf',
          extension: 'pdf'
        }]);
      if (!isEditing && !readOnly) setSignaturePositions([]);
        setActiveStep(2);
      } catch (error: any) {
        const data = error?.response?.data || {};
        const errors = (data.fieldErrors || []) as FieldError[];
        if (errors.length > 0) {
          setFieldErrors(errors.reduce((acc, fieldError) => ({ ...acc, [fieldError.fieldName]: fieldError.message }), {}));
        }
        ToastService.error(data.error || 'Không tạo được bản xem trước hợp đồng');
      } finally {
        setPreviewLoading(false);
      }
      return;
    }

    setActiveStep(prev => Math.min(prev + 1, createSteps.length - 1));
  };

  const buildPayload = () => {
    const data: Record<string, any> = {};
    fields.forEach(field => {
      data[field.fieldName] = fieldIsDate(field) ? toApiDate(formData[field.fieldName]) : (formData[field.fieldName] || '');
    });

    const activeSigners = signers
      .map((signer, index) => ({ ...signer, signerIndex: index }))
      .filter(signer => signer.partyId || signer.partyName || signer.emailAddress)
      .map(signer => ({
        ...signer,
        identityCard: signer.signerIndex === EMPLOYEE_SIGNER_INDEX
          ? (data['employee.idNumber'] || signer.identityCard || '')
          : (signer.identityCard || ''),
        roleTypeId: signer.roleTypeId || 'AgreementSigner',
        sequenceNum: signer.signerIndex === EMPLOYER_SIGNER_INDEX ? 1 : 2,
        signingOrder: signer.signerIndex === EMPLOYER_SIGNER_INDEX ? 1 : 2
      }));
    const signerByIndex = activeSigners.reduce((acc: Record<string, any>, signer) => {
      acc[String(signer.signerIndex)] = signer;
      return acc;
    }, {});
    const positions = signaturePositions
      .map((position, index) => {
        const signer = position.partyId
          ? activeSigners.find(item => String(item.partyId) === String(position.partyId))
          : signerByIndex[String(position.signerIndex)];
        if (!signer?.partyId) return null;
        const pageIndex = Number(position.pageIndex ?? Math.max(0, Number(position.pageNumber || 1) - 1));
        const pageCanvas = canvasRefs.current[pageIndex] || pageCanvases[pageIndex];
        return {
          partyId: signer.partyId,
          roleTypeId: 'AgreementSigner',
          locationSeqId: formatSeq(index + 1),
          signatureTypeEnumId: position.signatureTypeEnumId || 'AgtDigitalVerified',
          pageNumber: pageIndex + 1,
          pageWidth: Number(position.pageWidth || pageCanvas?.width) || 892,
          pageHeight: Number(position.pageHeight || pageCanvas?.height) || 1262,
          widthPx: Number(position.widthPx) || 200,
          heightPx: Number(position.heightPx) || 65,
          ulx: Number(position.x ?? position.ulx ?? 0),
          uly: Number(position.y ?? position.uly ?? 0),
          fontSize: Number(position.fontSize) || 12,
          partyName: signer.partyName || '',
          identityCard: signer.identityCard || '',
          emailAddress: signer.emailAddress || '',
          contactNumber: signer.contactNumber || '',
          representativeName: signer.representativeName || ''
        };
      })
      .filter(Boolean);

    const payload = {
      agreementName: agreementName || templateName,
      signingModeEnumId: 'AgtSignSequential',
      isUrgent: 'N',
      data,
      agreementParties: activeSigners,
      signaturePositions: positions
    };
    return isCloning ? sanitizeEmploymentContractClonePayload(payload, fields) : payload;
  };

  const getMutationErrorMessage = (error: any, fallback: string) => {
    if (!isCloning) return error?.response?.data?.error || fallback;
    const status = Number(error?.response?.status || 0);
    return status === 401 || status === 403
      ? 'Bạn không có quyền tạo hợp đồng lao động từ bản nhân bản này.'
      : fallback;
  };

  const continuePendingNavigation = () => {
    const pendingNavigation = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    clearUnsavedChanges();
    setExitDialogVisible(false);
    pendingNavigation?.();
  };

  const cancelPendingNavigation = () => {
    pendingNavigationRef.current = null;
    setExitDialogVisible(false);
  };

  const saveDraftAndExit = async () => {
    if (savingDraft) return;
    if (!templateCode) {
      ToastService.error('Vui lòng chọn mẫu hợp đồng trước khi lưu nháp');
      return;
    }

    setSavingDraft(true);
    try {
      const res = await EmploymentContractService.saveIncompleteDraft(
        isCloning ? undefined : agreementId,
        templateCode,
        buildPayload()
      );
      if (res?.success === 'N') {
        ToastService.error(isCloning
          ? 'Không lưu được bản nháp hợp đồng lao động từ bản nhân bản'
          : (res.error || 'Không lưu được bản nháp hợp đồng lao động'));
        return;
      }
      ToastService.success('Đã lưu bản nháp hợp đồng lao động');
      continuePendingNavigation();
    } catch (error: any) {
      ToastService.error(getMutationErrorMessage(error, 'Không lưu được bản nháp hợp đồng lao động'));
    } finally {
      setSavingDraft(false);
    }
  };

  const submit = async () => {
    if (submitInFlightRef.current || submitting) return;
    if (!templateCode) {
      ToastService.error('Vui lòng chọn mẫu hợp đồng');
      return;
    }
    if (!validateLocal()) {
      ToastService.error('Vui lòng nhập đủ thông tin bắt buộc');
      return;
    }

    submitInFlightRef.current = true;
    setSubmitting(true);
    try {
      const res = isEditing && agreementId
        ? await EmploymentContractService.updateDraftAgreement(agreementId, templateCode, buildPayload())
        : await EmploymentContractService.generateAgreement(templateCode, buildPayload());
      if (res?.success === 'N') {
        const errors = (res.fieldErrors || []) as FieldError[];
        if (errors.length > 0) {
          setFieldErrors(errors.reduce((acc, err) => ({ ...acc, [err.fieldName]: err.message }), {}));
        }
        ToastService.error(isCloning
          ? 'Không tạo được hợp đồng lao động từ bản nhân bản'
          : (res.error || (isEditing ? 'Không cập nhật được hợp đồng lao động' : 'Không tạo được hợp đồng lao động')));
        return;
      }
      ToastService.success(isEditing ? 'Đã cập nhật hợp đồng lao động' : 'Đã tạo hợp đồng lao động');
      clearUnsavedChanges();
      navigate('/hop-dong-lao-dong');
    } catch (error: any) {
      const data = error?.response?.data || {};
      const errors = (data.fieldErrors || []) as FieldError[];
      if (errors.length > 0) {
        setFieldErrors(errors.reduce((acc, err) => ({ ...acc, [err.fieldName]: err.message }), {}));
      }
      ToastService.error(isCloning
        ? getMutationErrorMessage(error, 'Không tạo được hợp đồng lao động từ bản nhân bản')
        : (data.error || (isEditing ? 'Không cập nhật được hợp đồng lao động' : 'Không tạo được hợp đồng lao động')));
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const renderField = (field: EmploymentContractField) => {
    const required = field.isRequired === 'Y';
    const value = formData[field.fieldName] || '';
    const error = fieldErrors[field.fieldName];

    return (
      <div className="col-12 md:col-6 xl:col-4" key={field.fieldName}>
        <label className="block mb-2 font-medium">
          {field.fieldLabel || field.fieldName}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {fieldIsDate(field) ? (
          <Calendar
            className={`w-full ${error ? 'p-invalid' : ''}`}
            value={value instanceof Date ? value : null}
            onChange={e => setFieldValue(field.fieldName, e.value)}
            dateFormat="dd/mm/yy"
            showIcon
            disabled={readOnly}
          />
        ) : field.fieldName.includes('otherAgreements') || field.fieldName.includes('raisePolicy') ? (
          <InputTextarea
            className={`w-full ${error ? 'p-invalid' : ''}`}
            value={value}
            rows={3}
            autoResize
            onChange={e => setFieldValue(field.fieldName, e.target.value)}
            disabled={readOnly}
          />
        ) : (
          <InputText
            className={`w-full ${error ? 'p-invalid' : ''}`}
            value={value}
            onChange={e => setFieldValue(field.fieldName, e.target.value)}
            disabled={readOnly}
          />
        )}
        {error && <small className="p-error block mt-1">{error}</small>}
      </div>
    );
  };

  const renderProgressSteps = (fullScreen = false) => (
    <div className={fullScreen ? 'surface-card border-bottom-1 surface-border p-3' : 'surface-card border-1 surface-border border-round p-3 mb-3'}>
      <div className="flex align-items-center justify-content-center gap-3 flex-wrap">
        {createSteps.map((step, index) => (
          <React.Fragment key={step.label}>
            <button
              type="button"
              className="appearance-none border-none bg-transparent p-0 cursor-pointer"
              onClick={() => index <= activeStep && setActiveStep(index)}
            >
              <span className="flex align-items-center gap-2">
                <span
                  className={`flex align-items-center justify-content-center border-circle ${
                    index <= activeStep ? 'bg-primary text-white' : 'surface-card text-500 border-1 surface-border'
                  }`}
                  style={{ width: 34, height: 34 }}
                >
                  {index + 1}
                </span>
                <span className={`font-semibold ${index === activeStep ? 'text-primary' : 'text-600'}`}>
                  <i className={`${step.icon} mr-2`} />
                  {step.label}
                </span>
              </span>
            </button>
            {index < createSteps.length - 1 && <span className="surface-border border-top-1" style={{ width: 90 }} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderSignerStep = () => {
    const employer = signers[EMPLOYER_SIGNER_INDEX] || emptySigner(1);
    const employee = signers[EMPLOYEE_SIGNER_INDEX] || emptySigner(2);
    const employeeSuggestions = customers.filter(customer =>
      isPersonCustomer(customer) && String(customer.partyId || customer.value) !== String(employer.partyId || '')
    );

    return (
      <div className="surface-card border-1 surface-border border-round p-3">
        <div className="mb-4">
          <div className="flex align-items-center gap-2 mb-3">
            <span className="flex align-items-center justify-content-center border-circle bg-primary text-white" style={{ width: 32, height: 32 }}>A</span>
            <span className="font-semibold">Bên A - Người sử dụng lao động</span>
            <Tag value="Mặc định" severity="info" />
          </div>
          <div className="grid">
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-medium">Công ty</label>
              <InputText className="w-full" value={employer.party?.label || employer.partyName || ''} disabled />
            </div>
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-medium">Địa chỉ</label>
              <InputText className="w-full" value={employer.address || ''} disabled />
            </div>
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-medium">Điện thoại</label>
              <InputText className="w-full" value={employer.contactNumber || ''} disabled />
            </div>
            <div className="col-12 md:col-3">
              <label className="block mb-2 font-medium">Đại diện</label>
              <InputText className="w-full" value={employer.representativeName || employer.partyName || ''} disabled />
            </div>
            <div className="col-12 md:col-3">
              <label className="block mb-2 font-medium">Chức vụ</label>
              <InputText className="w-full" value={employer.representativeTitle || ''} disabled />
            </div>
            <div className="col-12 md:col-2">
              <label className="block mb-2 font-medium">Quốc tịch</label>
              <InputText className="w-full" value={employer.representativeNationality || formData['company.representativeNationality'] || 'Việt Nam'} disabled />
            </div>
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-medium">Email</label>
              <InputText className="w-full" value={employer.emailAddress || ''} disabled />
            </div>
          </div>
        </div>

        <Divider />

        <div>
          <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div className="flex align-items-center gap-2">
              <span className="flex align-items-center justify-content-center border-circle bg-orange-400 text-white" style={{ width: 32, height: 32 }}>B</span>
              <span className="font-semibold">Bên B - Người lao động</span>
            </div>
            {!readOnly && <Button label="Tạo mới nhân viên" icon="pi pi-plus" size="small" outlined onClick={createEmployee} />}
          </div>
          <div className="grid">
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-medium">Ông/bà <span className="text-red-500">*</span></label>
              <AutoComplete
                className="w-full"
                inputClassName="w-full"
                dropdown
                value={employeeParty}
                suggestions={employeeSuggestions}
                completeMethod={searchCustomers}
                field="label"
                placeholder="Chọn người lao động"
                onChange={e => setEmployeeParty(e.value)}
                onSelect={e => selectPartyForSigner(EMPLOYEE_SIGNER_INDEX, e.value)}
                disabled={readOnly}
              />
            </div>
            <div className="col-12 md:col-3">
              <label className="block mb-2 font-medium">Quốc tịch</label>
              <InputText
                className="w-full"
                value={formData['employee.nationality'] || (isCloning ? '' : 'Việt Nam')}
                onChange={e => setFieldValue('employee.nationality', e.target.value)}
                disabled={readOnly}
              />
            </div>
            <div className="col-12 md:col-3">
              <label className="block mb-2 font-medium">Email</label>
              <InputText
                className="w-full"
                value={employee.emailAddress || ''}
                onChange={e => updateSigner(EMPLOYEE_SIGNER_INDEX, { emailAddress: e.target.value })}
                disabled={readOnly}
              />
            </div>
            <div className="col-12 md:col-2">
              <label className="block mb-2 font-medium">Điện thoại</label>
              <InputText
                className="w-full"
                value={employee.contactNumber || ''}
                onChange={e => updateSigner(EMPLOYEE_SIGNER_INDEX, { contactNumber: e.target.value })}
                disabled={readOnly}
              />
            </div>
            <div className="col-12 md:col-4">
              <label className="block mb-2 font-medium">Số CCCD</label>
              <InputText
                className="w-full"
                value={employee.identityCard || ''}
                onChange={e => setFieldValue('employee.idNumber', e.target.value)}
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDataStep = () => (
    <div className="grid m-0 h-full" style={{ minHeight: 0 }}>
      <div className="col-12 lg:col-3 xl:col-2 h-full">
        <div className="surface-card border-1 surface-border border-round p-3 h-full overflow-y-auto">
          <div className="mb-4">
            <label className="block mb-2 font-medium">Mẫu hợp đồng</label>
            <Dropdown
              className="w-full"
              value={templateCode}
              options={templates}
              optionLabel="templateName"
              optionValue="templateCode"
              valueTemplate={(option, props) => (
                <span className="block white-space-normal line-height-3 py-1 pr-2">
                  {option?.templateName || props.placeholder}
                </span>
              )}
              onChange={e => {
                markUnsavedChanges();
                setTemplateCode(e.value);
              }}
              placeholder="Chọn mẫu"
              disabled={readOnly || isCloning}
            />
          </div>
          <div>
            <label className="block mb-2 font-medium">Tên tài liệu</label>
            <InputTextarea
              className="w-full"
              value={agreementName}
              rows={2}
              autoResize
              onChange={e => {
                markUnsavedChanges();
                setAgreementName(e.target.value);
              }}
              disabled={readOnly}
            />
          </div>
          <Divider />
          <div className="mb-4">
            <div className="flex align-items-center justify-content-between gap-2 mb-2">
              <span className="font-semibold">Tiến độ dữ liệu</span>
              <span className="text-sm font-semibold text-primary">
                {fieldProgress.completed}/{fieldProgress.total}
              </span>
            </div>
            <div className="surface-200 border-round overflow-hidden" style={{ height: 8 }}>
              <div
                className="h-full bg-primary border-round transition-all transition-duration-300"
                style={{ width: `${fieldProgress.percentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 lg:col-9 xl:col-10 h-full" style={{ minHeight: 0 }}>
        {formTemplateHtml || formTemplateLoading ? (
          <EmploymentContractHtmlForm
            html={formTemplateHtml}
            fields={fields}
            values={formData}
            errors={fieldErrors}
            loading={formTemplateLoading}
            readOnly={readOnly}
            onChange={setFieldValue}
          />
        ) : (
          Object.entries(groupedFields).map(([group, groupFields]) => (
            <div className="surface-card border-1 surface-border border-round p-3 mb-3" key={group}>
              <div className="font-semibold text-lg mb-3">{groupLabels[group] || group}</div>
              <div className="grid">{groupFields.map(renderField)}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderSignatureStep = () => (
    <div className="h-full border-1 surface-border border-round overflow-hidden" style={{ minHeight: 0 }}>
      <Step3Positions
        documentRecipients={documentRecipients}
        signaturePositions={signaturePositions}
        selectedSigner={selectedSigner}
        setSelectedSigner={setSelectedSigner}
        canvasLoading={canvasLoading}
        pageCanvases={pageCanvases}
        canvasRefs={canvasRefs}
        draggableRefs={draggableRefs}
        handleDrop={handleDrop}
        handleDragStop={handleDragStop}
        selectedSignatureType={selectedSignatureType}
        setSelectedSignatureType={setSelectedSignatureType}
        removeSignaturePosition={removeSignaturePosition}
        setSignaturePositions={setSignaturePositionsWithDirty}
        normalizeLocationSeq={normalizeLocationSeq}
        mainFiles={previewMainFiles}
        activeMainFileIndex={0}
        onSetActiveFile={() => undefined}
        isViewMode={readOnly}
        enableZoom
      />
    </div>
  );

  const renderStepContent = () => {
    if (activeStep === 0) return renderSignerStep();
    if (activeStep === 1) return renderDataStep();
    return renderSignatureStep();
  };

  const refreshDetailSigning = () => setDetailRefreshKey(current => current + 1);

  const renderActionFooter = (fullScreen = false) => (
    <div className={`${fullScreen ? 'surface-card border-top-1 surface-border p-3' : 'surface-card border-1 surface-border border-round p-3 mt-3'} flex align-items-center justify-content-between`}>
      <Button
        label={activeStep === 0 ? 'Quay lại danh sách' : 'Quay lại'}
        icon="pi pi-arrow-left"
        outlined
        onClick={() => activeStep === 0 ? navigate('/hop-dong-lao-dong') : setActiveStep(prev => prev - 1)}
      />
      {activeStep < createSteps.length - 1 ? (
        <Button
          label="Tiếp theo"
          icon="pi pi-arrow-right"
          iconPos="right"
          loading={activeStep === 1 && previewLoading}
          onClick={goNext}
        />
      ) : readOnly ? (
        <div className="flex align-items-center justify-content-end flex-wrap gap-2">
          {renderSigningStatus(
            signers[EMPLOYER_SIGNER_INDEX]?.partyStatusId === 'AGT_PTY_SIGNED',
            signers[EMPLOYEE_SIGNER_INDEX]?.partyStatusId === 'AGT_PTY_SIGNED'
          )}
          {!isSigningClosed(editDraftRef.current?.agreement?.statusId)
            && signers[EMPLOYER_SIGNER_INDEX]?.partyStatusId !== 'AGT_PTY_SIGNED' && (
            <Button
              label="Ký hợp đồng"
              icon="pi pi-file-check"
              onClick={() => setDetailSigningAction('employer-sign')}
            />
          )}
          {!isSigningClosed(editDraftRef.current?.agreement?.statusId)
            && signers[EMPLOYER_SIGNER_INDEX]?.partyStatusId === 'AGT_PTY_SIGNED'
            && signers[EMPLOYEE_SIGNER_INDEX]?.partyStatusId !== 'AGT_PTY_SIGNED' && (
            <Button
              label="Gửi email"
              icon="pi pi-send"
              onClick={() => setDetailSigningAction('send-email')}
            />
          )}
        </div>
      ) : (
        <Button
          label={isEditing ? 'Lưu thay đổi' : 'Tạo tài liệu'}
          icon={isEditing ? 'pi pi-save' : 'pi pi-check'}
          loading={submitting}
          disabled={submitting}
          onClick={submit}
        />
      )}
    </div>
  );

  if (isCloning && cloneLoadState !== 'ready') {
    return (
      <Dialog
        visible
        maximized
        modal
        blockScroll
        showHeader={false}
        closable={false}
        closeOnEscape={false}
        draggable={false}
        className="employment-contract-workspace-dialog"
        contentClassName="p-0 overflow-hidden"
        style={{ width: '100vw', height: '100vh', maxHeight: '100vh', margin: 0, borderRadius: 0 }}
        onHide={() => navigate('/hop-dong-lao-dong')}
      >
        <div className="flex align-items-center justify-content-center h-full surface-section p-4">
          {cloneLoadState === 'loading' ? (
            <div className="flex flex-column align-items-center gap-3" role="status" aria-live="polite">
              <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="4" />
              <span className="font-medium text-600">Đang chuẩn bị bản nhân bản...</span>
            </div>
          ) : (
            <div className="surface-card border-1 surface-border border-round p-4 w-full" style={{ maxWidth: 560 }}>
              <Message severity="error" text={cloneError?.message || 'Không thể chuẩn bị bản nhân bản.'} className="w-full mb-4" />
              <div className="flex align-items-center justify-content-end flex-wrap gap-2">
                <Button
                  label="Quay lại danh sách"
                  icon="pi pi-arrow-left"
                  outlined
                  onClick={() => navigate('/hop-dong-lao-dong')}
                />
                <Button
                  label="Thử lại"
                  icon="pi pi-refresh"
                  onClick={() => setCloneRetryKey(current => current + 1)}
                />
              </div>
            </div>
          )}
        </div>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        visible
        maximized
        modal
        blockScroll
        showHeader={false}
        closable={false}
        closeOnEscape={false}
        draggable={false}
        className="employment-contract-workspace-dialog"
        contentClassName="p-0 overflow-hidden"
        style={{ width: '100vw', height: '100vh', maxHeight: '100vh', margin: 0, borderRadius: 0 }}
        onHide={() => navigate('/hop-dong-lao-dong')}
      >
        <div className="flex flex-column h-full surface-section">
          <div className="relative flex-none">
            {renderProgressSteps(true)}
            <Button
              icon="pi pi-times"
              text
              rounded
              severity="secondary"
              aria-label="Đóng"
              className="absolute"
              style={{ right: '1rem', top: '50%', transform: 'translateY(-50%)' }}
              onClick={() => navigate('/hop-dong-lao-dong')}
            />
          </div>
          {isCloning && (
            <Message
              severity="info"
              className="flex-none w-full border-noround m-0"
              text={`Bạn đang tạo hợp đồng mới từ “${cloneSource.agreementName || 'Hợp đồng nguồn'}”${cloneSource.agreementNo ? ` (${cloneSource.agreementNo})` : ''}. Hợp đồng nguồn sẽ không bị thay đổi.`}
            />
          )}
          <div
            className={`flex-1 overflow-y-auto ${activeStep === createSteps.length - 1 ? 'p-0' : 'p-4'}`}
            style={{ minHeight: 0 }}
          >
            <div
              className={activeStep === createSteps.length - 1 ? 'w-full h-full' : `mx-auto ${activeStep === 1 ? 'h-full' : ''}`}
              style={activeStep === createSteps.length - 1
                ? { height: '100%' }
                : { maxWidth: '1600px', ...(activeStep === 1 ? { height: '100%' } : {}) }}
            >
              {renderStepContent()}
            </div>
          </div>
          <div className="flex-none">
            {renderActionFooter(true)}
          </div>
        </div>
      </Dialog>
      {agreementId && (
        <EmploymentContractSigningDialog
          visible={Boolean(detailSigningAction)}
          action={detailSigningAction || 'employer-sign'}
          agreement={{
            agreementId,
            agreementName
          }}
          onHide={() => setDetailSigningAction(null)}
          onSuccess={refreshDetailSigning}
        />
      )}
      <Dialog
        header="Lưu bản nháp?"
        visible={exitDialogVisible}
        modal
        closable={!savingDraft}
        closeOnEscape={!savingDraft}
        draggable={false}
        style={{ width: 'min(32rem, calc(100vw - 2rem))' }}
        onHide={cancelPendingNavigation}
        footer={(
          <div className="flex align-items-center justify-content-between flex-wrap gap-2">
            <Button
              label="Tiếp tục sửa"
              icon="pi pi-pencil"
              outlined
              disabled={savingDraft}
              onClick={cancelPendingNavigation}
            />
            <div className="flex align-items-center gap-2">
              <Button
                label="Không lưu"
                icon="pi pi-times"
                severity="danger"
                outlined
                disabled={savingDraft}
                onClick={continuePendingNavigation}
              />
              <Button
                label="Lưu nháp"
                icon="pi pi-save"
                loading={savingDraft}
                onClick={saveDraftAndExit}
              />
            </div>
          </div>
        )}
      >
        <div className="flex align-items-start gap-3">
          <i className="pi pi-exclamation-triangle text-orange-500 text-2xl mt-1" />
          <div>
            <div className="font-semibold mb-2">Bạn đang có dữ liệu chưa lưu.</div>
            <div className="text-600 line-height-3">
              Bạn có muốn lưu phần thông tin hiện tại thành bản nháp trước khi rời màn hình không?
            </div>
          </div>
        </div>
      </Dialog>
      {customerDialog.render()}
    </>
  );
};

export default EmploymentContract;
